// WikiVersionPanel.jsx — version history timeline (right panel) + side-by-side diff.
import React, { useEffect, useState } from 'react';
import { History, X, GitCompare, ArrowLeft } from 'lucide-react';
import { useWikiApi } from '../../hooks/useWiki';
import { relativeTime } from '../../lib/wikiConstants';
import { blockKey } from './blocks/wikiBlockRegistry';
import WikiBlock from './blocks/WikiBlock';

export default function WikiVersionPanel({ pageId, onClose, onCompare }) {
    const api = useWikiApi();
    const [data, setData] = useState(null);
    useEffect(() => { let m = true; api.versions(pageId).then(d => m && setData(d)).catch(() => m && setData({ versions: [] })); return () => { m = false; }; }, [pageId]); // eslint-disable-line
    const versions = data?.versions || [];

    return (
        <div className="w-[320px] border-l border-stone-200 bg-white flex flex-col h-full">
            <div className="h-12 px-4 flex items-center gap-2 border-b border-stone-200">
                <History className="w-4 h-4 text-stone-900" />
                <span className="text-[13px] wiki-display font-bold text-stone-900 flex-1">Version history</span>
                <button onClick={onClose} className="w-7 h-7 rounded hover:bg-stone-100 text-stone-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto p-3">
                {versions.length < 2 && <div className="text-[12px] text-stone-500 px-1 py-2">Only one version exists for this page.</div>}
                <div className="relative pl-4">
                    <div className="absolute left-[5px] top-1 bottom-1 w-px bg-stone-200" />
                    {versions.map((v, i) => (
                        <div key={v.v} className="relative mb-3">
                            <div className={`absolute -left-[14px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${v.is_current ? 'bg-stone-900' : 'bg-stone-300'}`} />
                            <div className="rounded-lg border border-stone-200 p-2.5">
                                <div className="flex items-center gap-2">
                                    <span className="text-[12px] font-bold text-stone-900 font-mono">v{v.v}</span>
                                    {v.is_current && <span className="text-[10px] font-semibold px-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Current</span>}
                                    <span className="text-[11px] text-stone-500 ml-auto">{relativeTime(v.created_at)}</span>
                                </div>
                                <div className="text-[12px] text-stone-600 mt-1">{v.label}</div>
                                <div className="text-[11px] text-stone-500 mt-0.5">by {v.author_name || '—'}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {versions.length >= 2 && (
                <div className="p-3 border-t border-stone-200">
                    <button onClick={() => onCompare(versions[1].v, versions[0].v)}
                        className="w-full h-9 rounded-lg bg-stone-900 text-white text-[12px] font-semibold inline-flex items-center justify-center gap-1.5">
                        <GitCompare className="w-3.5 h-3.5" /> Compare v{versions[1].v} → v{versions[0].v}
                    </button>
                </div>
            )}
        </div>
    );
}

export function WikiDiffView({ pageId, a, b, onBack }) {
    const api = useWikiApi();
    const [diff, setDiff] = useState(null);
    useEffect(() => { let m = true; api.diff(pageId, a, b).then(d => m && setDiff(d)).catch(() => m && setDiff(null)); return () => { m = false; }; }, [pageId, a, b]); // eslint-disable-line

    if (!diff) return <div className="h-full flex items-center justify-center text-stone-400 text-[13px]">Loading comparison…</div>;
    const older = diff.a, newer = diff.b;
    const oldKeys = new Set((older.blocks || []).map(blockKey));
    const newKeys = new Set((newer.blocks || []).map(blockKey));

    return (
        <div className="h-full flex flex-col bg-stone-50/40">
            <div className="h-12 px-5 flex items-center gap-3 border-b border-stone-200 bg-white">
                <button onClick={onBack} className="h-8 px-2.5 rounded-lg hover:bg-stone-100 text-stone-600 text-[12px] font-semibold inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Back to page</button>
                <span className="text-[13px] wiki-display font-bold text-stone-900">Comparing versions</span>
                <div className="flex-1" />
                <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><span className="w-2.5 h-2.5 rounded-sm bg-rose-100 border border-rose-200" /> removed</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-200" /> added</span>
            </div>
            <div className="flex-1 overflow-auto grid grid-cols-2 gap-0">
                <div className="border-r border-stone-200 p-6">
                    <div className="flex items-center gap-2 mb-3"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 font-mono">v{older.v}</span><span className="text-[12px] text-stone-500">{relativeTime(older.created_at)} · {older.author_name}</span></div>
                    {(older.blocks || []).map((blk, i) => <WikiBlock key={i} b={blk} diffTone={newKeys.has(blockKey(blk)) ? null : 'removed'} />)}
                </div>
                <div className="p-6 bg-white">
                    <div className="flex items-center gap-2 mb-3"><span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">v{newer.v}</span><span className="text-[12px] text-stone-500">{relativeTime(newer.created_at)} · {newer.author_name}</span><span className="ml-auto text-[10px] font-semibold px-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Current</span></div>
                    {(newer.blocks || []).map((blk, i) => <WikiBlock key={i} b={blk} diffTone={oldKeys.has(blockKey(blk)) ? null : 'added'} />)}
                </div>
            </div>
        </div>
    );
}
